import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} TubVariant
 * @property {number} id
 * @property {string} size - Size option value
 * @property {string} flavor - Flavor option value
 * @property {number} price - In minor units (cents)
 * @property {boolean} available
 * @property {{ id: number, src: string } | null} media
 * @property {Array<{ plan: number, price: number }>} [allocs]
 *
 * @typedef {object} TubData
 * @property {number} productId
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {number} sizeIndex
 * @property {number} flavorIndex
 * @property {string} moneyFormat
 * @property {number} threshold - Free-shipping threshold in cents
 * @property {string} flatRate
 * @property {number} cartSubtotal - Cart subtotal at render time (cents)
 * @property {string} shipsFreeLabel
 * @property {{ free: string, progress: string, subscription: string, sizeSwitch: string, flavorSwitch: string }} messages
 * @property {Array<{ value: string, handle: string, short: string }>} sizes
 * @property {Array<{ value: string, handle: string }>} flavors
 * @property {TubVariant[]} variants
 * @property {Array<{ id: number, name: string, percent: number }>} [plans]
 */

/**
 * @typedef {object} TubPdpRefs
 * @property {HTMLElement} [flavorGroup]
 * @property {HTMLButtonElement[]} [flavorChips]
 * @property {HTMLElement} [sizeGroup]
 * @property {HTMLButtonElement[]} [sizeCards]
 * @property {HTMLElement} [ladderNote]
 * @property {HTMLElement} [purchaseType]
 * @property {HTMLInputElement[]} [purchaseRadios]
 * @property {HTMLSelectElement} [planSelect]
 * @property {HTMLElement} [shippingLine]
 * @property {HTMLElement} [shippingMsg]
 * @property {HTMLElement} [gapFillers]
 * @property {HTMLElement} [xxlNotice]
 */

/**
 * Custom flavor/size selector for the tub PDP.
 *
 * Owns selection state and drives the theme's native components — media
 * gallery, sticky add-to-cart bar, product form — by dispatching the theme's
 * own `variant:update` event with a freshly fetched document, exactly like
 * the native variant picker does (see docs/tub-pdp-recon.md). It never forks
 * the theme's product form: the form's hidden variant input and the URL are
 * kept in sync synchronously, so add-to-cart is always correct even while a
 * re-render fetch is in flight.
 *
 * @extends {Component<TubPdpRefs>}
 */
class TubPdp extends Component {
  /** @type {TubData} */
  #data;

  /** @type {string} */
  #sizeValue = '';

  /** @type {string} */
  #flavorValue = '';

  /** @type {string | null} */
  #sellingPlanId = null;

  /** @type {number} */
  #qty = 1;

  /** @type {number} */
  #cartSubtotal = 0;

  /** @type {string} */
  #prevFlavor = '';

  /** @type {AbortController | undefined} */
  #fetchAbort;

  #listeners = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('script[data-tub-data]');
    if (!dataScript?.textContent) return;
    this.#data = JSON.parse(dataScript.textContent);

    this.#sizeValue = this.dataset.selectedSize ?? '';
    this.#flavorValue = this.dataset.selectedFlavor ?? '';
    this.#prevFlavor = this.#flavorValue;
    this.#cartSubtotal = this.#data.cartSubtotal || 0;

    const { signal } = this.#listeners;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChanged, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartChanged, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    this.addEventListener('submit', this.#onFormSubmit, { signal, capture: true });
    this.refs.sizeGroup?.addEventListener('scroll', this.#onRailScroll, { signal, passive: true });
    window.addEventListener('resize', this.#onRailScroll, { signal });

    this.#initFromUrl();
    this.#refreshCartSubtotal();

    // make sure the default (or deep-linked) size card is visible in the
    // mobile rail once layout has settled — rail-only, never the page
    requestAnimationFrame(() => {
      this.#revealSelectedCard(false);
      this.#onRailScroll();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners.abort();
    this.#fetchAbort?.abort();
  }

  // ---------- variant matrix ----------

  /**
   * @param {string} size
   * @param {string} flavor
   * @returns {TubVariant | undefined}
   */
  variantFor(size, flavor) {
    return this.#data.variants.find((v) => v.size === size && v.flavor === flavor);
  }

  /** @returns {TubVariant | undefined} */
  get currentVariant() {
    return this.variantFor(this.#sizeValue, this.#flavorValue);
  }

  /**
   * Sizes (option values) a flavor exists in, in size order.
   * @param {string} flavor
   */
  #sizesFor(flavor) {
    return this.#data.sizes.filter((s) => this.variantFor(s.value, flavor));
  }

  /**
   * Nearest valid size for a flavor, preferring the smallest index distance
   * from the current size and bumping up on ties. Sold-out combinations are
   * only chosen when no in-stock size carries the flavor — never dead-end
   * the buyer on a sold-out variant when an available one exists.
   * @param {string} flavor
   * @returns {string | null}
   */
  #nearestValidSize(flavor) {
    const sizes = this.#data.sizes;
    const currentIdx = sizes.findIndex((s) => s.value === this.#sizeValue);
    let best = null;
    let bestScore = Infinity;
    sizes.forEach((s, idx) => {
      const variant = this.variantFor(s.value, flavor);
      if (!variant) return;
      const score = Math.abs(idx - currentIdx) * 2 + (idx < currentIdx ? 1 : 0) + (variant.available ? 0 : 100);
      if (score < bestScore) {
        bestScore = score;
        best = s.value;
      }
    });
    return best;
  }

  /** @param {string} value */
  #sizeShort(value) {
    return this.#data.sizes.find((s) => s.value === value)?.short ?? value.split(' ')[0];
  }

  /** Display form of a flavor for notes: lowercased, parenthetical dropped. @param {string} value */
  #flavorDisplay(value) {
    return value.split('(')[0].trim().toLowerCase();
  }

  /** @param {string} flavor @param {string} joiner */
  #sizesList(flavor, joiner = ' and ') {
    const shorts = this.#sizesFor(flavor).map((s) => s.short);
    if (shorts.length <= 2) return shorts.join(joiner);
    return shorts.join(', ');
  }

  /** Chip micro-tag for a flavor missing in some sizes, e.g. "XL & XXL only". @param {string} flavor */
  #sizesOnlyNote(flavor) {
    const valid = this.#sizesFor(flavor);
    if (valid.length === 0 || valid.length >= this.#data.sizes.length) return '';
    return `${this.#sizesList(flavor, ' & ')} only`;
  }

  /** @param {number} cents */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return formatMoney(Math.round(cents), this.#data.moneyFormat, currency);
  }

  // ---------- selection handlers (declarative on: attributes) ----------

  /** @param {Event} event */
  onFlavorTap(event) {
    const chip = /** @type {HTMLElement} */ (event.target);
    const value = chip?.dataset?.flavorValue;
    if (value) this.selectFlavor(value);
  }

  /** @param {Event} _event */
  onRestockClick(_event) {
    this.#track('tub:restock_click');
  }

  /** @param {Event} event */
  onSizeTap(event) {
    const card = /** @type {HTMLElement} */ (event.target);
    const value = card?.dataset?.sizeValue;
    if (value) this.selectSize(value);
  }

  /** @param {KeyboardEvent} event */
  onFlavorKeydown(event) {
    this.#radioKeydown(event, this.refs.flavorChips ?? [], (el) => {
      const value = el.dataset.flavorValue;
      if (value) this.selectFlavor(value);
    });
  }

  /** @param {KeyboardEvent} event */
  onSizeKeydown(event) {
    this.#radioKeydown(event, this.refs.sizeCards ?? [], (el) => {
      const value = el.dataset.sizeValue;
      if (value) this.selectSize(value);
    });
  }

  /**
   * Roving-tabindex arrow navigation for a radiogroup.
   * @param {KeyboardEvent} event
   * @param {HTMLElement[]} items
   * @param {(el: HTMLElement) => void} select
   */
  #radioKeydown(event, items, select) {
    if (items.length === 0) return;
    const current = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current < 0 ? 0 : (current + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current < 0 ? 0 : (current - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = items[next];
    if (!target) return;
    target.focus();
    select(target);
  }

  /**
   * Select a flavor. If the flavor doesn't exist in the current size,
   * auto-bump to the nearest valid size and say so — never dead-end.
   * @param {string} value
   */
  selectFlavor(value) {
    if (value === this.#flavorValue) return;
    let note = '';

    if (this.variantFor(this.#sizeValue, this.#flavorValue)) {
      this.#prevFlavor = this.#flavorValue;
    }

    if (!this.variantFor(this.#sizeValue, value)) {
      const bumped = this.#nearestValidSize(value);
      if (!bumped) return;
      note = this.#data.messages.sizeSwitch
        .replace('[size]', this.#sizeShort(bumped))
        .replace('[flavor]', this.#flavorDisplay(value))
        .replace('[sizes]', this.#sizesList(value));
      this.#sizeValue = bumped;
    }

    this.#flavorValue = value;
    this.#commit(note);
    this.#track('tub:flavor_select');
  }

  /**
   * Select a size. If the current flavor doesn't exist in it, revert the
   * flavor to the previous valid one with the mirrored note.
   * @param {string} value
   */
  selectSize(value) {
    if (value === this.#sizeValue) return;
    let note = '';

    if (!this.variantFor(value, this.#flavorValue)) {
      const blocked = this.#flavorValue;
      let fallback = this.variantFor(value, this.#prevFlavor) ? this.#prevFlavor : null;
      if (!fallback) {
        fallback =
          this.#data.flavors.find((f) => this.variantFor(value, f.value)?.available)?.value ??
          this.#data.flavors.find((f) => this.variantFor(value, f.value))?.value ??
          null;
      }
      if (!fallback) return;
      note = this.#data.messages.flavorSwitch
        .replace('[flavor]', this.#flavorDisplay(fallback))
        .replace('[blocked]', this.#flavorDisplay(blocked))
        .replace('[sizes]', this.#sizesList(blocked));
      this.#flavorValue = fallback;
    }
    // #prevFlavor is maintained only by selectFlavor — a size move must not
    // clobber the flavor the buyer had before an auto-bump (e.g. XL→XXL while
    // on 3-way split must still revert to their pre-split flavor later)

    this.#sizeValue = value;
    this.#commit(note);
    this.#track('tub:size_select');
  }

  // ---------- rendering ----------

  /**
   * Apply the current selection everywhere: optimistic UI first, then the
   * theme-native re-render via variant:update.
   * @param {string} [note]
   * @param {{ scroll?: boolean }} [options]
   */
  #commit(note = '', { scroll = true } = {}) {
    const variant = this.currentVariant;

    this.dataset.selectedSize = this.#sizeValue;
    this.dataset.selectedFlavor = this.#flavorValue;

    this.#renderChips();
    this.#renderCards();
    this.#renderPurchaseType();
    this.#renderShipping();
    this.#renderXxlNotice();

    const noteEl = this.refs.ladderNote;
    if (noteEl) noteEl.textContent = note;

    // keep the form correct synchronously — add-to-cart must never race the fetch
    const idInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );
    if (idInput && variant) idInput.value = String(variant.id);
    const atcButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('product-form-component [ref="addToCartButton"]')
    );
    if (atcButton) atcButton.disabled = !variant || !variant.available;

    const restock = /** @type {HTMLElement | undefined} */ (this.refs.restockLink);
    if (restock) restock.hidden = !(variant && !variant.available);

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.delete('flavor');
      url.searchParams.delete('size');
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    }

    // reveal the selected card by scrolling the rail itself — unlike
    // scrollIntoView this can never scroll the page vertically, so it is
    // safe for deep links too (instant there, smooth for user taps)
    this.#revealSelectedCard(scroll);

    if (variant) this.#fetchAndDispatch(variant);
  }

  /**
   * Horizontally center the selected size card inside the scroll-snap rail.
   * No-ops in the desktop grid (nothing overflows) and never moves the page.
   * @param {boolean} smooth
   */
  #revealSelectedCard(smooth) {
    const rail = this.refs.sizeGroup;
    const card = (this.refs.sizeCards ?? []).find((c) => c.dataset.sizeValue === this.#sizeValue);
    if (!rail || !card) return;
    if (rail.scrollWidth <= rail.clientWidth + 1) return;
    const left = card.offsetLeft - rail.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2;
    rail.scrollTo({
      left: Math.max(0, left),
      behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
    });
  }

  /**
   * Show the "More sizes" hint only while the rail actually has more cards
   * off-screen to the right.
   */
  #onRailScroll = () => {
    const rail = this.refs.sizeGroup;
    const hint = /** @type {HTMLElement | undefined} */ (this.refs.sizeHint);
    if (!rail || !hint) return;
    const overflows = rail.scrollWidth > rail.clientWidth + 1;
    const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 24;
    hint.hidden = !overflows || atEnd;
  };

  #renderChips() {
    for (const chip of this.refs.flavorChips ?? []) {
      const flavor = chip.dataset.flavorValue ?? '';
      const variant = this.variantFor(this.#sizeValue, flavor);
      const state = !variant ? 'incompatible' : variant.available ? 'ok' : 'soldout';
      const selected = flavor === this.#flavorValue;

      chip.dataset.state = state;
      chip.setAttribute('aria-checked', String(selected));
      chip.tabIndex = selected ? 0 : -1;

      const micro = chip.querySelector('[data-ref="micro"]');
      if (micro) {
        micro.textContent =
          state === 'soldout'
            ? chip.dataset.soldoutText || 'Sold out'
            : state === 'incompatible'
              ? this.#sizesOnlyNote(flavor)
              : chip.dataset.badge || '';
      }
    }
  }

  #renderCards() {
    for (const card of this.refs.sizeCards ?? []) {
      const size = card.dataset.sizeValue ?? '';
      const variant = this.variantFor(size, this.#flavorValue);
      const state = !variant ? 'incompatible' : variant.available ? 'ok' : 'soldout';
      const selected = size === this.#sizeValue;
      const quarts = Number(card.dataset.quarts) || 1;

      card.dataset.state = state;
      card.setAttribute('aria-checked', String(selected));
      card.tabIndex = selected ? 0 : -1;

      const price = card.querySelector('[data-ref="price"]');
      if (price) price.textContent = variant ? this.#money(variant.price) : '—';

      const ppq = card.querySelector('[data-ref="ppq"]');
      if (ppq) ppq.textContent = variant ? `${this.#money(variant.price / quarts)}/qt` : '';

      const free = /** @type {HTMLElement | null} */ (card.querySelector('[data-ref="shipsFree"]'));
      if (free) free.hidden = !variant || variant.price < this.#data.threshold;

      const micro = card.querySelector('[data-ref="micro"]');
      if (micro) {
        micro.textContent =
          state === 'soldout' ? 'Sold out'
          : state === 'incompatible' ? 'Not in this flavor'
          : card.dataset.substitutionMicro || '';
      }
    }
  }

  // ---------- purchase type (selling plans) ----------

  /** Allocation price in cents for the current variant under a plan. @param {number} planId */
  #planPrice(planId) {
    const variant = this.currentVariant;
    if (!variant) return 0;
    const alloc = (variant.allocs ?? []).find((a) => a.plan === planId);
    if (alloc) return alloc.price;
    const plan = (this.#data.plans ?? []).find((p) => p.id === planId);
    if (plan?.percent) return Math.round(variant.price * (1 - plan.percent / 100));
    return variant.price;
  }

  #renderPurchaseType() {
    const variant = this.currentVariant;
    if (!this.refs.purchaseType || !variant) return;

    const oneTime = this.querySelector('[data-ref="oneTimePrice"]');
    if (oneTime) oneTime.textContent = this.#money(variant.price);

    const planId = this.#sellingPlanId
      ? Number(this.#sellingPlanId)
      : Number((this.refs.purchaseRadios ?? []).find((r) => r.value !== '')?.value ?? 0);
    const planPrice = this.querySelector('[data-ref="planPrice"]');
    if (planPrice && planId) planPrice.textContent = this.#money(this.#planPrice(planId));
  }

  /** @param {Event} event */
  onPlanChange(event) {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    this.#sellingPlanId = radio.value || null;

    for (const other of this.refs.purchaseRadios ?? []) {
      const row = other.closest('.tub-purchase__row');
      if (row instanceof HTMLElement) row.dataset.selected = String(other.checked);
    }

    this.#syncSellingPlanInput();
    this.#renderPurchaseType();
    this.#renderShipping();
    this.#track('tub:plan_select', { sellingPlanId: this.#sellingPlanId });
  }

  /** @param {Event} event */
  onPlanIntervalChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const subscribeRadio = (this.refs.purchaseRadios ?? []).find((r) => r.value !== '');
    if (subscribeRadio) {
      subscribeRadio.value = select.value;
      if (subscribeRadio.checked) this.#sellingPlanId = select.value;
    }
    this.#syncSellingPlanInput();
    this.#renderPurchaseType();
    this.#track('tub:plan_select', { sellingPlanId: this.#sellingPlanId });
  }

  #syncSellingPlanInput() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-tub-selling-plan]'));
    if (!input) return;
    if (this.#sellingPlanId) {
      input.value = this.#sellingPlanId;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  }

  // ---------- shipping line + gap filler ----------

  #renderShipping() {
    const { shippingMsg, gapFillers, shippingLine } = this.refs;
    if (!shippingMsg) return;

    if (this.#sellingPlanId) {
      shippingMsg.textContent = this.#data.messages.subscription;
      if (gapFillers) gapFillers.hidden = true;
      if (shippingLine) shippingLine.dataset.under = 'false';
      return;
    }

    const variant = this.currentVariant;
    const selectionTotal = (variant ? variant.price : 0) * this.#qty;
    const gap = this.#data.threshold - (selectionTotal + this.#cartSubtotal);
    const under = gap > 0;

    shippingMsg.textContent = under
      ? this.#data.messages.progress.replace('[amount]', this.#money(gap)).replace('[flat]', this.#data.flatRate)
      : this.#data.messages.free;
    if (gapFillers) gapFillers.hidden = !under;
    if (shippingLine) shippingLine.dataset.under = String(under);
  }

  // Substitution notice (see snippets/xxl-substitution-notice.liquid): visible
  // only while the affected size is selected. Wired into #commit rather than
  // the tub:size_select event so flavor-driven auto size bumps and ?size=
  // deep links toggle it too.
  #renderXxlNotice() {
    const notice = this.refs.xxlNotice;
    if (!notice) return;
    const handle = this.#data.sizes.find((s) => s.value === this.#sizeValue)?.handle;
    notice.hidden = handle !== notice.dataset.xxlSizeHandle;
  }

  /** @param {Event} _event */
  #onCartChanged = (_event) => {
    const event = /** @type {CustomEvent} */ (_event);
    const total = event.detail?.resource?.total_price;
    if (typeof total === 'number') {
      this.#cartSubtotal = total;
      this.#renderShipping();
    } else {
      this.#refreshCartSubtotal();
    }
  };

  async #refreshCartSubtotal() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();
      if (typeof cart.total_price === 'number') {
        this.#cartSubtotal = cart.total_price;
        this.#renderShipping();
      }
    } catch {
      /* shipping line simply keeps its last state */
    }
  }

  /** One-tap add for gap-filler and cross-sell chips. @param {Event} event */
  async onQuickAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    button.disabled = true;

    try {
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');
      const cartItemsSections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);
      if (cartItemsSections.length > 0) formData.set('sections', cartItemsSections.join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      if (result.status) throw new Error(result.message || 'could not add to cart');

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      this.#cartSubtotal = cart.total_price ?? this.#cartSubtotal;

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'tub-pdp', {
          source: 'tub-pdp',
          itemCount: cart.item_count,
          variantId,
          sections: result.sections,
        })
      );

      button.dataset.added = 'true';
      setTimeout(() => {
        button.removeAttribute('data-added');
        button.disabled = false;
      }, 2000);

      this.#renderShipping();
      this.#track('tub:gapfill_add', {
        addedVariantId: Number(variantId),
        addedPrice: Number(button.dataset.price) / 100,
        context: button.dataset.context || 'gap-filler',
      });
    } catch (error) {
      console.error('tub-pdp: quick add failed', error);
      button.disabled = false;
    }
  }

  // ---------- theme-native re-render ----------

  /**
   * Fetch the page for the resolved variant and dispatch the theme's
   * variant:update so the native gallery, sticky bar and product form morph
   * themselves — same contract as the native variant picker.
   * @param {TubVariant} variant
   */
  async #fetchAndDispatch(variant) {
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();

    const resource = {
      id: variant.id,
      available: variant.available,
      featured_media: variant.media ? { id: variant.media.id, preview_image: { src: variant.media.src } } : null,
    };

    let html;
    try {
      const url = `${this.#data.productUrl.split('?')[0]}?variant=${variant.id}`;
      const response = await fetch(url, { signal: this.#fetchAbort.signal });
      html = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // still dispatch so listeners sync from the resource; empty doc no-ops the morphs
      html = new DOMParser().parseFromString('', 'text/html');
    }

    this.dispatchEvent(
      new VariantUpdateEvent(resource, String(variant.id), {
        html,
        productId: this.dataset.productId ?? '',
      })
    );

    if (!variant.media) this.#syncGalleryToFlavor();
  }

  /**
   * Alt-text fallback for flavor media: when the variant has no featured
   * media, select the gallery slide whose image alt contains
   * "flavor:<handleized-flavor>" — the tag can ride at the end of a
   * descriptive, SEO-friendly alt text (convention in the recon doc).
   */
  #syncGalleryToFlavor() {
    const gallery = /** @type {any} */ (this.querySelector('media-gallery'));
    const slideshow = gallery?.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slides || slides.length === 0) return;

    const handle = this.#data.flavors.find((f) => f.value === this.#flavorValue)?.handle;
    if (!handle) return;
    const tag = `flavor:${handle}`;

    const index = Array.from(slides).findIndex((slide) => {
      const img = /** @type {HTMLImageElement | null} */ (slide.querySelector('img[alt]'));
      return img ? img.alt.trim().toLowerCase().includes(tag) : false;
    });

    if (index >= 0) {
      slideshow.select(index, undefined, { animate: !prefersReducedMotion() });
    }
  }

  // ---------- URL contract ----------

  /**
   * On load: `?variant=` is already resolved server-side (legacy nav links
   * keep working). `?flavor=` / `?size=` match handleized option values;
   * a flavor param must not change the default size unless required for
   * validity.
   */
  #initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('variant')) return;

    const flavorParam = params.get('flavor');
    const sizeParam = params.get('size');
    if (!flavorParam && !sizeParam) return;

    const flavor = flavorParam ? this.#data.flavors.find((f) => f.handle === flavorParam.toLowerCase()) : null;
    const size = sizeParam ? this.#data.sizes.find((s) => s.handle === sizeParam.toLowerCase()) : null;

    let nextSize = size?.value ?? this.#sizeValue;
    const nextFlavor = flavor?.value ?? this.#flavorValue;

    if (!this.variantFor(nextSize, nextFlavor)) {
      const valid = this.#nearestValidSize(nextFlavor);
      if (valid) nextSize = valid;
    }

    if (nextSize === this.#sizeValue && nextFlavor === this.#flavorValue) return;
    this.#sizeValue = nextSize;
    this.#flavorValue = nextFlavor;
    this.#commit('', { scroll: false });
  }

  // ---------- quantity + analytics ----------

  /** @param {Event} _event */
  #onQuantityUpdate = (_event) => {
    const event = /** @type {CustomEvent} */ (_event);
    if (event.detail?.cartLine) return;
    this.#qty = Number(event.detail?.quantity) || 1;
    this.#renderShipping();
  };

  /** @param {Event} event */
  #onFormSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    this.#track('tub:atc', { sellingPlanId: this.#sellingPlanId });
  };

  /**
   * Dispatch an analytics CustomEvent and mirror it into the dataLayer.
   * @param {string} name
   * @param {Record<string, unknown>} [extra]
   */
  #track(name, extra = {}) {
    const variant = this.currentVariant;
    const sizeEntry = this.#data.sizes.find((s) => s.value === this.#sizeValue);
    const card = (this.refs.sizeCards ?? []).find((c) => c.dataset.sizeValue === this.#sizeValue);
    const quarts = Number(card?.dataset.quarts) || 0;
    const detail = {
      size: this.#sizeValue,
      sizeShort: sizeEntry?.short ?? '',
      flavor: this.#flavorValue,
      variantId: variant?.id ?? null,
      price: variant ? variant.price / 100 : null,
      perQuart: variant && quarts ? Math.round(variant.price / quarts) / 100 : null,
      quantity: this.#qty,
      ...extra,
    };
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...detail });
    }
  }
}

if (!customElements.get('tub-pdp')) {
  customElements.define('tub-pdp', TubPdp);
}
